import React, { useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  Stack,
  Paper,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";

const OrdersFilter = ({ onFilter, isMobile = false, serviceTypes = [] }) => {
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    serviceType: "",
  });
  const { t } = useTranslation();

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    onFilter(filters);
  };

  const handleReset = () => {
    const resetFilters = {
      fromDate: "",
      toDate: "",
      serviceType: "",
    };
    setFilters(resetFilters);
    onFilter(resetFilters);
  };

  if (isMobile) {
    return (
      <Paper sx={{ p: 2, mb: 3, borderRadius: "12px" }} elevation={2}>
        <Stack gap={2}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              {t('filter_title')}
            </Typography>
          </Box>

          {/* Date Range */}
          <Stack gap={2}>
            <Box>
              <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
                {t('filter_fromDate')}
              </Typography>
              <TextField
                fullWidth
                type="date"
                size="small"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
                {t('filter_toDate')}
              </Typography>
              <TextField
                fullWidth
                type="date"
                size="small"
                value={filters.toDate}
                onChange={(e) => handleFilterChange("toDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Stack>

          {/* Service Type Dropdown */}
          <Box>
              <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
                {t('filter_requestType')}
              </Typography>
            <TextField
              fullWidth
              select
              size="small"
              value={filters.serviceType}
              onChange={(e) => handleFilterChange("serviceType", e.target.value)}
              placeholder={t('filter_select')}
            >
              <MenuItem value="">{t('filter_select')}</MenuItem>
              {serviceTypes.map((service, index) => (
                <MenuItem key={index} value={service}>
                  {service}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Buttons */}
          <Stack direction="row" gap={2} mt={1}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              sx={{ borderRadius: "8px", }}
            >
              {t('filter_apply')}
             <SearchIcon sx={{
                mx: 1,
                fontSize: { xs: 18 }
              }}/>
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleReset}
              sx={{ borderRadius: "8px" }}
            >
              {t('filter_reset')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  // Desktop version
  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: "12px" }} elevation={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <FilterListIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {t('filter_title')}
          </Typography>
        </Box>
        <Tooltip title={t('filter_reset')} arrow>
          <IconButton
            onClick={handleReset}
            color="primary"
            size="small"
            sx={{
              "&:hover": {
                backgroundColor: "primary.light",
                color: "white",
              },
            }}
          >
            <ClearIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr) auto" },
          gap: 2,
          alignItems: "end",
        }}
      >
        {/* From Date */}
        <Box>
          <Typography variant="caption" sx={{ mb: 0.5, display: "block", fontWeight: 500 }}>
            {t('filter_fromDate')}
          </Typography>
          <TextField
            fullWidth
            type="date"
            size="small"
            value={filters.fromDate}
            onChange={(e) => handleFilterChange("fromDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ backgroundColor: "white" }}
          />
        </Box>

        {/* To Date */}
        <Box>
          <Typography variant="caption" sx={{ mb: 0.5, display: "block", fontWeight: 500 }}>
            {t('filter_toDate')}
          </Typography>
          <TextField
            fullWidth
            type="date"
            size="small"
            value={filters.toDate}
            onChange={(e) => handleFilterChange("toDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ backgroundColor: "white" }}
          />
        </Box>

        {/* Service Type */}
        <Box>
          <Typography variant="caption" sx={{ mb: 0.5, display: "block", fontWeight: 500 }}>
            {t('filter_requestType')}
          </Typography>
          <TextField
            fullWidth
            select
            size="small"
            value={filters.serviceType}
            onChange={(e) => handleFilterChange("serviceType", e.target.value)}
            sx={{ backgroundColor: "white" }}
          >
            <MenuItem value="">{t('filter_select')}</MenuItem>
            {serviceTypes.map((service, index) => (
              <MenuItem key={index} value={service}>
                {service}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Search Button */}
        <Button
          variant="contained"
          onClick={handleSearch}
          sx={{ height: 40, minWidth: 110, borderRadius: "8px" }}
        >
          {t('filter_apply')}
          <SearchIcon sx={{
                mx: 1,
                fontSize: { xs: 18 }
              }}/>
        </Button>
      </Box>
    </Paper>
  );
};

export default OrdersFilter;
